import React, { useEffect } from 'react'
import { connect, useSelector } from 'react-redux'
import Container from '../components/layout/Container'
import Page from '../components/layout/Page'
import { ApplicationState } from '../store'
import { fetchError, fetchRequest, fetchSuccess, selectDate } from '../store/availableTimes/actions'
import { Loading } from '../components/data/Loading'
import { getEmployeeAvailableTimes } from '../api/availableTimes'
import { Service } from '../store/services/types'
import { Employee } from '../store/employees/types'
import { AvailableTimes } from '../components/availableTimes'
import { GoBackButton } from '../components/layout/GoBackButton'
import { RouteType } from '../types/routes'
import { useLoadSalonData } from '../hooks/useLoadSalonData'
import { ErrorResponse } from '../types/auth'
import { AvailableWeek } from '../store/availableTimes/types'
import { Box } from '../components/layout/Box'
import { Typography } from '../components/layout/Typography'
import { ActionButton } from '../styles/components/buttons'
import { getLocalDateIsoString } from '../utils/date'

interface PropsFromState {
  loading: boolean
  errors?: string
  selectedDate: Date
  selectedServices?: Service[]
  selectedEmployee?: Employee
}

// We can use `typeof` here to map our dispatch types to the props, like so.
interface PropsFromDispatch {
  fetchAvailableTimes: typeof fetchRequest
  fetchSuccess: typeof fetchSuccess
  fetchError: typeof fetchError
  selectDate: typeof selectDate
}

// Combine both state + dispatch props - as well as any props we want to pass - in a union type.
type AllProps = PropsFromState & PropsFromDispatch

const MAXIMUM_AVAILABLE_TIMES_FETCH_RETRY = 10

let availableTimesFetchRetryCount = 0

const AvailableTimesPage: React.FC<AllProps> = ({
  loading,
  errors,
  selectedDate,
  selectedServices,
  selectedEmployee,
  fetchAvailableTimes: dispatchFetchAvailableTimes,
  fetchSuccess: dispatchFetchSuccess,
  fetchError: dispatchFetchError,
  selectDate: dispatchSelectDate
}) => {
  const getTotalAppointmentDuration = () => {
    if (!selectedServices) return 0
    return selectedServices.reduce((total, service) => total + service.minuten, 0)
  }

  const getNextWeekDateObject = (date: Date, newSelectedWeek: 'prev' | 'next') => {
    const nextWeek = new Date(date)

    const extraDays = newSelectedWeek === 'prev' ? -7 : 7

    nextWeek.setDate(nextWeek.getDate() + extraDays)

    return nextWeek
  }

  const getAvailableTimesFromSelectedDate = async (dateToGetAvailableTimeFrom = selectedDate) => {
    dispatchFetchAvailableTimes()

    if (selectedEmployee === undefined) {
      dispatchFetchError('No employee selected')
      return
    }

    const availableWeek = await getEmployeeAvailableTimes({
      employeeGuid: selectedEmployee.guid,
      selectedDate: dateToGetAvailableTimeFrom.toISOString(),
      appointmentDuration: getTotalAppointmentDuration()
    })

    if (availableWeek === null) {
      dispatchFetchError('No available times found')
      return
    }

    if ('status' in availableWeek && availableWeek.status === 'Error') {
      dispatchFetchError((availableWeek as ErrorResponse<'Error'>).message)
      return
    }

    const firstAvailableDateOfWeek = Object.entries(availableWeek).find(([_, availableTimes]) => availableTimes.length > 0)

    if (firstAvailableDateOfWeek === undefined) {
      if (availableTimesFetchRetryCount < MAXIMUM_AVAILABLE_TIMES_FETCH_RETRY) {
        availableTimesFetchRetryCount += 1
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        getNextWeekAvailableTimes('next', dateToGetAvailableTimeFrom)
      } else {
        dispatchFetchError('No available times found')
        availableTimesFetchRetryCount = 0
      }

      return
    }

    availableTimesFetchRetryCount = 0
    dispatchFetchError('')
    dispatchFetchSuccess(availableWeek as AvailableWeek)
    const newSelectedDate = new Date(firstAvailableDateOfWeek[0])
    dispatchSelectDate(newSelectedDate)
  }

  const getNextWeekAvailableTimes = (newSelectedWeek: 'prev' | 'next', currentSelectedDate = selectedDate) => {
    const nextWeek = getNextWeekDateObject(currentSelectedDate, newSelectedWeek)
    getAvailableTimesFromSelectedDate(nextWeek)
  }

  useLoadSalonData({ redirectTo: RouteType.Home })

  const salonData = useSelector((state: ApplicationState) => state.salon.data)

  useEffect(() => {
    if (salonData !== undefined) {
      getAvailableTimesFromSelectedDate()
    }
  }, [])

  return (
    <Page>
      <Container>
        {errors ? (
          <Box margin="1rem 0 0">
            <Typography variant="body1">{errors}</Typography>
            <Box margin="1rem 0 0" flex justifyContent="center">
              <ActionButton onClick={() => getAvailableTimesFromSelectedDate(new Date(getLocalDateIsoString()))}>Zurück</ActionButton>
            </Box>
          </Box>
        ) : (
          <>
            {loading ? (
              <Loading />
            ) : (
              <>
                <GoBackButton title="Leistungen" />

                <AvailableTimes getNextWeekAvailableTimes={getNextWeekAvailableTimes} />
              </>
            )}
          </>
        )}
      </Container>
    </Page>
  )
}

const mapStateToProps = ({ availableTimes, services, employees }: ApplicationState) => ({
  loading: availableTimes.loading,
  errors: availableTimes.errors,
  selectedDate: availableTimes.selectedDate,
  selectedServices: services.selected,
  selectedEmployee: employees.selected
})

const mapDispatchToProps = {
  fetchAvailableTimes: fetchRequest,
  fetchSuccess,
  fetchError,
  selectDate
}

export default connect(mapStateToProps, mapDispatchToProps)(AvailableTimesPage)
